import React from "react";
import {
  Card,
  CardContent,
  Avatar,
  Typography,
  Divider,
  Box,
  Chip,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { API_BASE_URL } from "../../../utils/api";
import { Button } from "@mui/material";


// Icons
import EmailIcon from "@mui/icons-material/Email";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import NatureIcon from "@mui/icons-material/Nature";
import BugReportIcon from "@mui/icons-material/BugReport";

const typeIcons = {
  "Building/Maintenance": (
    <BuildCircleIcon fontSize="small" sx={{ mr: 1, color: "primary.main" }} />
  ),
  Landscaping: (
    <NatureIcon fontSize="small" sx={{ mr: 1, color: "success.main" }} />
  ),
  Other: <BugReportIcon fontSize="small" sx={{ mr: 1, color: "grey.600" }} />,
};



const MemberProfileCard = ({ member, unresolvedTickets = [] }) => {
  const joinDate = member.JoinDate
    ? new Date(member.JoinDate).toLocaleDateString()
    : "N/A";
  const email = member.EmailAddress?.trim() || "N/A";
  const address = member.FullAddress?.trim() || "N/A";
  const mapsLink =
    address !== "N/A"
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          address
        )}`
      : null;

  const phone = member.PhoneNumber?.trim() || null;
  const altPhone = member.AlternatePhoneNumber?.trim() || null;
  const cellPhone = member.CellPhoneNumber?.trim() || null;

  // ✅ Group unresolved tickets by type
  const ticketsByType = unresolvedTickets.reduce((acc, ticket) => {
    const type = ticket.type_name || "Other"; // use type_name from API
    if (!acc[type]) acc[type] = [];
    acc[type].push(ticket);
    return acc;
  }, {});

  return (
    <Card sx={{ maxWidth: 420, mx: "auto", borderRadius: 3, boxShadow: 4 }}>
      <CardContent sx={{ textAlign: "center" }}>
        <Avatar
          src={`${API_BASE_URL}/images/${encodeURIComponent(
            member.PhotoGraphicFileName?.trim() || ""
          )}`}
          alt={member.FirstName}
          sx={{
            width: 120,
            height: 120,
            margin: "0 auto",
            marginBottom: 2,
            border: "4px solid #007bff",
          }}
        />

        <Typography variant="h5" fontWeight="bold">
          {`${member.FirstName} ${member.LastName}`}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Member Number: {member.ClubMemberCode}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* 🔹 Member Info Section */}
        <Box sx={{ textAlign: "left", pl: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <CalendarTodayIcon sx={{ color: "text.secondary", mr: 1 }} />
            <Typography variant="body2">Join Date: {joinDate}</Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <EmailIcon sx={{ color: "text.secondary", mr: 1 }} />
            {email !== "N/A" ? (
              <Typography
                variant="body2"
                component="a"
                href={`mailto:${email}`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                Email: {email}
              </Typography>
            ) : (
              <Typography variant="body2">Email: N/A</Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <LocationOnIcon sx={{ color: "text.secondary", mr: 1 }} />
            {mapsLink ? (
              <Typography
                variant="body2"
                component="a"
                href={mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none", color: "inherit" }}
              >
                Address: {address}
              </Typography>
            ) : (
              <Typography variant="body2">Address: N/A</Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <PhoneIcon sx={{ color: "text.secondary", mr: 1 }} />
            <Typography variant="body2">
              {phone ? `Phone: ${phone}` : "Phone: N/A"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <PhoneAndroidIcon sx={{ color: "text.secondary", mr: 1 }} />
            <Typography variant="body2">
              {altPhone ? `Alt Phone: ${altPhone}` : "Alt Phone: N/A"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SmartphoneIcon sx={{ color: "text.secondary", mr: 1 }} />
            <Typography variant="body2">
              {cellPhone ? `Cell: ${cellPhone}` : "Cell: N/A"}
            </Typography>
          </Box>
        </Box>

        {/* 🔹 Unresolved Tickets Section */}
        <Divider sx={{ my: 2 }} />
        {unresolvedTickets.length > 0 ? (
          <>
            <Typography variant="h6" sx={{ textAlign: "center", mb: 1 }}>
              Other Open Tickets
            </Typography>
            {Object.entries(ticketsByType).map(([type, tickets]) => (
  <Box
    key={type}
    sx={{
      textAlign: "left",
      pl: 2,
      mb: 2,
      p: 1.5,
      borderRadius: 2,
      border: "1px solid",
      borderColor: "grey.300",   
      bgcolor: "transparent",    
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
      {typeIcons[type] || (
        <BuildCircleIcon fontSize="small" sx={{ mr: 1, color: "primary.main" }} />
      )}
      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
        {type}: {tickets.length} open
      </Typography>
    </Box>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, ml: 4 }}>
      {tickets.map((t) => (
        <Button
          key={t.order_id}
          component={RouterLink}
          to={`/home/workorders/${t.order_id}`}
          size="small"
          variant="contained"  
          color="primary"
          sx={{
            borderRadius: "20px",
            fontSize: "0.75rem",
            textTransform: "none",
            px: 2,
            py: 0.5,
            minWidth: "auto"
          }}
        >
          WO-{t.order_id}
        </Button>
      ))}
    </Box>
  </Box>
))}
          </>
        ) : (
          <Typography
            variant="body2"
            sx={{ textAlign: "left", mt: 1, color: "success.main", pl: 4 }}
          >
            🎉 No other open tickets
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default MemberProfileCard;
